import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Download, Mail } from 'lucide-react';
import SendEmailModal from '../../components/SendEmailModal';
import DeliveryOrderPrint from './DeliveryOrderPrint';
import { generateDocPdf } from '../../utils/generateDocPdf';
import { useToast } from '../../hooks/use-toast';

const DeliveryOrderForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printContainerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  const [custodians, setCustodians] = useState([]);

  const [formData, setFormData] = useState({
    job_no: '',
    date: '',
    to: '',
    deliver_to: '',
    airline: '',
    do_no: '',
    do_date: '',
    igm_no: '',
    igm_date: '',
    flight_no: '',
    flight_date: '',
    airport_of_load: '',
    airport_of_discharge: '',
    mawb_no: '',
    mawb_date: '',
    mawb_packages: '',
    mawb_gross_weight: '',
    mawb_description: '',
    hawb_no: '',
    hawb_date: '',
    hawb_packages: '',
    hawb_gross_weight: '',
    hawb_description: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const docketResponse = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, {
          headers: getAuthHeaders()
        });
        if (!docketResponse.ok) throw new Error('Failed to fetch docket data');
        const docketData = await docketResponse.json();
        setDocketInfo(docketData);

        // Fetch custodians for quick-fill 'To' party
        let custs = [];
        try {
          const custResponse = await fetch(`${backendUrl}/api/custodians?is_active=true`, {
            headers: getAuthHeaders()
          });
          if (custResponse.ok) {
            custs = await custResponse.json();
            setCustodians(custs);
          }
        } catch (e) {
          console.error('Failed to load custodians:', e);
        }

        // Check if DO already exists
        const doResponse = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/DELIVERY_ORDER`, {
          headers: getAuthHeaders()
        });

        if (doResponse.ok) {
          const doData = await doResponse.json();
          if (doData && doData.id) {
            const savedData = { ...(doData.document_data || {}) };
            // If savedData had old bug where to === deliver_to, fix to to show Custodian
            if (savedData.to && savedData.to === savedData.deliver_to) {
              const defaultCust = custs[0];
              savedData.to = defaultCust
                ? [defaultCust.name, defaultCust.address].filter(Boolean).join('\n')
                : `The Manager / Custodian\nAir Cargo Complex${docketData?.resolved_shipment?.discharge_at ? `\n${docketData.resolved_shipment.discharge_at}` : ''}`;
            }
            if (!savedData.airline && (docketData?.resolved_shipment?.carrier || docketData?.docket?.payload_json?.shipment_info?.carrier)) {
              savedData.airline = docketData?.resolved_shipment?.carrier || docketData?.docket?.payload_json?.shipment_info?.carrier;
            }
            setFormData(savedData);
            setDocumentId(doData.id);
            setIsSaved(true);
          } else {
            populateFromDocket(docketData, custs);
          }
        } else {
          populateFromDocket(docketData, custs);
        }
      } catch (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };

    if (docketId) fetchData();
  }, [docketId, backendUrl, toast]);

  const populateFromDocket = (docketData, custsList = []) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload = docket.payload_json || {};
    const shipmentInfo = payload.shipment_info || {};
    const invoices = payload.invoices || [];
    const invoice = invoices[0] || {};

    const consigneeData = resolved_parties?.importer_receiver;
    let consigneeText = '';
    if (consigneeData) {
      consigneeText = [
        consigneeData.name,
        consigneeData.branch_name,
        consigneeData.address,
        consigneeData.phone ? `Tel: ${consigneeData.phone}` : '',
        consigneeData.email ? `Email: ${consigneeData.email}` : ''
      ].filter(Boolean).join('\n');
    }

    // Default 'To' party is the Custodian / Cargo Terminal Operator
    let toText = '';
    const activeCusts = (custsList && custsList.length > 0) ? custsList : custodians;
    if (activeCusts && activeCusts.length > 0) {
      const defaultCust = activeCusts[0];
      toText = [defaultCust.name, defaultCust.address].filter(Boolean).join('\n');
    } else {
      toText = `The Manager / Custodian\nAir Cargo Complex${resolved_shipment?.discharge_at ? `\n${resolved_shipment.discharge_at}` : ''}`;
    }

    setFormData({
      job_no: docket.system_docket_no || '',
      date: new Date().toISOString().split('T')[0],
      to: toText,
      deliver_to: consigneeText,
      airline: resolved_shipment?.carrier || shipmentInfo.carrier || '',
      do_no: '',
      do_date: new Date().toISOString().split('T')[0],
      igm_no: shipmentInfo.igm_no || '',
      igm_date: shipmentInfo.igm_date || '',
      flight_no: shipmentInfo.flight_no || '',
      flight_date: shipmentInfo.flight_date || '',
      airport_of_load: resolved_shipment?.loading_from || '',
      airport_of_discharge: resolved_shipment?.discharge_at || '',
      mawb_no: shipmentInfo.mawb_no || '',
      mawb_date: shipmentInfo.mawb_date || '',
      mawb_packages: invoice.no_of_packages || '',
      mawb_gross_weight: invoice.gross_weight || '',
      mawb_description: invoice.description || shipmentInfo.hawb_description || '',
      hawb_no: shipmentInfo.hawb_no || '',
      hawb_date: shipmentInfo.hawb_date || '',
      hawb_packages: invoice.no_of_packages || '',
      hawb_gross_weight: invoice.gross_weight || '',
      hawb_description: invoice.description || shipmentInfo.hawb_description || ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const url = isSaved
        ? `${backendUrl}/api/dockets/${docketId}/documents/DELIVERY_ORDER`
        : `${backendUrl}/api/dockets/${docketId}/documents`;

      const method = isSaved ? 'PUT' : 'POST';

      const body = isSaved
        ? { document_data: formData }
        : { document_type: 'DELIVERY_ORDER', document_data: formData };

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }

      const savedData = await response.json();
      setDocumentId(savedData.id);
      setIsSaved(true);

      toast({ title: 'Success', description: 'Delivery Order saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.open(`/dockets/${docketId}/do-print`, '_blank');
  };

  const handleSavePDF = () => {
    window.open(`/dockets/${docketId}/do-print`, '_blank');
  };

  const pdfFetcher = async () => {
    if (!printContainerRef.current) return null;
    const el = printContainerRef.current;
    el.style.left = '0';
    await new Promise(r => setTimeout(r, 1500));
    try {
      return await generateDocPdf(el);
    } finally {
      el.style.left = '-9999px';
    }
  };

  // Print view
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-600" />
                Air Delivery Order
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.docket?.system_docket_no || docketId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSaved && (
              <>
                <Button variant="outline" onClick={handleSavePDF} data-testid="do-save-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Save PDF
                </Button>
                <Button variant="outline" onClick={handlePrint} data-testid="do-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" onClick={() => setShowEmailModal(true)}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50" data-testid="do-send-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Send Mail
                </Button>
              </>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="do-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Form */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">DELIVERY ORDER</h2>
          </div>

          <div className="p-5 space-y-5">

            {/* Row 1: Job No, Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Job No.</Label>
                <Input
                  value={formData.job_no}
                  readOnly
                  className="mt-1 bg-gray-50 cursor-not-allowed"
                  data-testid="do-job-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="mt-1"
                  data-testid="do-date"
                />
              </div>
            </div>

            {/* Row 2: To (Custodian / Terminal), Deliver To (Consignee) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">To (Custodian / Terminal)</Label>
                  {custodians.length > 0 && (
                    <select
                      className="text-[11px] border border-gray-300 rounded px-1.5 py-0.5 text-text-secondary bg-white cursor-pointer hover:border-gray-400"
                      onChange={(e) => {
                        const sel = custodians.find(c => c.id === e.target.value);
                        if (sel) {
                          const parts = [sel.name];
                          if (sel.address) parts.push(sel.address);
                          handleChange('to', parts.filter(Boolean).join('\n'));
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select Custodian...</option>
                      {custodians.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <Textarea
                  value={formData.to}
                  onChange={(e) => handleChange('to', e.target.value)}
                  className="mt-1 h-24"
                  placeholder="The Manager / Custodian, Cargo Terminal..."
                  data-testid="do-to"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Please deliver to (Consignee / Importer)</Label>
                <Textarea
                  value={formData.deliver_to}
                  onChange={(e) => handleChange('deliver_to', e.target.value)}
                  className="mt-1 h-24"
                  placeholder="Consignee / Importer delivery address"
                  data-testid="do-deliver-to"
                />
              </div>
            </div>

            {/* Row 3: D/O No, D/O Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">D/O No.</Label>
                <Input
                  value={formData.do_no}
                  onChange={(e) => handleChange('do_no', e.target.value)}
                  className="mt-1"
                  placeholder="Delivery Order number"
                  data-testid="do-do-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">D/O Date</Label>
                <Input
                  type="date"
                  value={formData.do_date}
                  onChange={(e) => handleChange('do_date', e.target.value)}
                  className="mt-1"
                  data-testid="do-do-date"
                />
              </div>
            </div>

            {/* Row 4: IGM No, IGM Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">IGM No.</Label>
                <Input
                  value={formData.igm_no}
                  onChange={(e) => handleChange('igm_no', e.target.value)}
                  className="mt-1"
                  data-testid="do-igm-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">IGM Date</Label>
                <Input
                  type="date"
                  value={formData.igm_date}
                  onChange={(e) => handleChange('igm_date', e.target.value)}
                  className="mt-1"
                  data-testid="do-igm-date"
                />
              </div>
            </div>

            {/* Row 5: Flight Name/Airline, Flight No, Flight Date */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight Name / Airline</Label>
                <Input
                  value={formData.airline || ''}
                  onChange={(e) => handleChange('airline', e.target.value)}
                  className="mt-1"
                  placeholder="Airline / Flight Name"
                  data-testid="do-airline"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight No.</Label>
                <Input
                  value={formData.flight_no}
                  onChange={(e) => handleChange('flight_no', e.target.value)}
                  className="mt-1"
                  data-testid="do-flight-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight Date</Label>
                <Input
                  type="date"
                  value={formData.flight_date}
                  onChange={(e) => handleChange('flight_date', e.target.value)}
                  className="mt-1"
                  data-testid="do-flight-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Load</Label>
                <Input
                  value={formData.airport_of_load}
                  onChange={(e) => handleChange('airport_of_load', e.target.value)}
                  className="mt-1"
                  data-testid="do-airport-load"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Discharge</Label>
                <Input
                  value={formData.airport_of_discharge}
                  onChange={(e) => handleChange('airport_of_discharge', e.target.value)}
                  className="mt-1"
                  data-testid="do-airport-discharge"
                />
              </div>
            </div>

            {/* Divider: MAWB section */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide font-ibm mb-3">MAWB Details</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB No.</Label>
                  <Input
                    value={formData.mawb_no}
                    onChange={(e) => handleChange('mawb_no', e.target.value)}
                    className="mt-1"
                    data-testid="do-mawb-no"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB Date</Label>
                  <Input
                    type="date"
                    value={formData.mawb_date}
                    onChange={(e) => handleChange('mawb_date', e.target.value)}
                    className="mt-1"
                    data-testid="do-mawb-date"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">No. of Packages</Label>
                  <Input
                    value={formData.mawb_packages}
                    onChange={(e) => handleChange('mawb_packages', e.target.value)}
                    className="mt-1"
                    data-testid="do-mawb-packages"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Gross Weight</Label>
                  <Input
                    value={formData.mawb_gross_weight}
                    onChange={(e) => handleChange('mawb_gross_weight', e.target.value)}
                    className="mt-1"
                    data-testid="do-mawb-gross-weight"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Description</Label>
                  <Input
                    value={formData.mawb_description}
                    onChange={(e) => handleChange('mawb_description', e.target.value)}
                    className="mt-1"
                    data-testid="do-mawb-description"
                  />
                </div>
              </div>
            </div>

            {/* Divider: HAWB section */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide font-ibm mb-3">HAWB Details</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB No.</Label>
                  <Input
                    value={formData.hawb_no}
                    onChange={(e) => handleChange('hawb_no', e.target.value)}
                    className="mt-1"
                    data-testid="do-hawb-no"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB Date</Label>
                  <Input
                    type="date"
                    value={formData.hawb_date}
                    onChange={(e) => handleChange('hawb_date', e.target.value)}
                    className="mt-1"
                    data-testid="do-hawb-date"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">No. of Packages</Label>
                  <Input
                    value={formData.hawb_packages}
                    onChange={(e) => handleChange('hawb_packages', e.target.value)}
                    className="mt-1"
                    data-testid="do-hawb-packages"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Gross Weight</Label>
                  <Input
                    value={formData.hawb_gross_weight}
                    onChange={(e) => handleChange('hawb_gross_weight', e.target.value)}
                    className="mt-1"
                    data-testid="do-hawb-gross-weight"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-text-secondary font-ibm">Description</Label>
                  <Input
                    value={formData.hawb_description}
                    onChange={(e) => handleChange('hawb_description', e.target.value)}
                    className="mt-1"
                    data-testid="do-hawb-description"
                  />
                </div>
              </div>
            </div>

          </div>
        </Card>

        {/* Saved status indicator */}
        {isSaved && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-blue-700 font-ibm">
              <span className="font-semibold">Saved</span> — Delivery Order linked to docket {docketInfo?.docket?.system_docket_no}
            </p>
            <span className="text-xs text-blue-600">Document ID: {documentId?.toString().slice(0, 8)}...</span>
          </div>
        )}

      </div>
    </div>

    {/* Hidden print container for PDF generation */}
    {isSaved && (
      <div
        ref={printContainerRef}
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', background: '#fff', zIndex: -1 }}
      >
        <DeliveryOrderPrint docketId={docketId} formData={formData} />
      </div>
    )}

    <SendEmailModal
      isOpen={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      templateKey="delivery_order"
      variables={{
        docket_no: docketInfo?.docket?.system_docket_no || '',
        consignee_name: (formData.consignee || '').split('\n')[0],
        do_no: formData.do_no || '',
        description: formData.description || '',
      }}
      defaultToEmail={docketInfo?.resolved_parties?.importer_receiver?.email || ''}
      defaultSubject={`Delivery Order — ${docketInfo?.docket?.system_docket_no || ''}`}
      pdfFetcher={isSaved ? pdfFetcher : null}
      pdfFilename={`DO_${docketInfo?.docket?.system_docket_no || docketId}.pdf`}
      documentTitle={`Delivery Order — ${docketInfo?.docket?.system_docket_no || ''}`}
      docketId={docketId}
    />
    </>
  );
};

export default DeliveryOrderForm;
